import React, { useState, useEffect, useCallback, useMemo } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import TabGroup from "@/components/ui/tab";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowRight as ArrowRightIcon,
  ArrowDown as ArrowDownIcon,
  Department as DepartmentIcon,
} from "@/components/icons";
import type { FilterField, TableColumn } from "@/types/component";
import type {
  Department,
  CreateDepartmentPayload,
} from "../types/departments.types";
import { departmentsApi } from "../services/departments.api";
import DepartmentModal from "../components/DepartmentModal";
import { getAdminUsers } from "@/lib/users.api";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./DepartmentsPage.module.scss";

const ORG_NAME = i18n.t("departmentsPage.orgName");
const VIRTUAL_ROOT_ID = "__org_root__";
const VIRTUAL_CENTRAL_ID = "__group_central__";
const VIRTUAL_BRANCH_ID = "__group_branch__";

type TreeRow = {
  id: string;
  name: string;
  depth: number;
  type?: number;
  parentID?: string | null;
  parentName?: string;
  description?: string;
  childCount: number;
  isVirtual?: boolean;
  dept?: Department;
};

const DepartmentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.DEPARTMENTS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.DEPARTMENTS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.DEPARTMENTS_DELETE);

  const [tree, setTree] = useState<Department[]>([]);
  const [flatAll, setFlatAll] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [activeTab, setActiveTab] = useState(1);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState<Department | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [supervisors, setSupervisors] = useState<
    { id: string; name: string }[]
  >([]);
  const [supervisorSearch, setSupervisorSearch] = useState("");
  const [debouncedSupervisorSearch, setDebouncedSupervisorSearch] =
    useState("");
  const [locations, setLocations] = useState<
    { id: string; name: string; address?: string }[]
  >([]);
  const [locationSearch, setLocationSearch] = useState("");
  const [debouncedLocationSearch, setDebouncedLocationSearch] = useState("");

  const isHierarchyTab = activeTab === 1;
  const isSearchMode = debouncedSearchTerm.trim() !== "";

  const flattenTree = useCallback((nodes: Department[]): Department[] => {
    const out: Department[] = [];
    const walk = (list: Department[]) => {
      list.forEach((n) => {
        out.push(n);
        if (n.children && n.children.length > 0) walk(n.children);
      });
    };
    walk(nodes);
    return out;
  }, []);

  const fetchTree = useCallback(
    async (search?: string) => {
      setIsLoading(true);
      try {
        const data = await departmentsApi.getTree(search);
        setTree(data);
        setFlatAll(flattenTree(data));
      } catch {
        setTree([]);
        setFlatAll([]);
      } finally {
        setIsLoading(false);
      }
    },
    [flattenTree],
  );

  useEffect(() => {
    fetchTree(debouncedSearchTerm);
  }, [fetchTree, debouncedSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchSupervisors = useCallback(async (search?: string) => {
    try {
      const { data } = await getAdminUsers({
        page: 1,
        limit: 50,
        search: search?.trim(),
      });
      setSupervisors(
        data.map((u) => ({
          id: u.id,
          name:
            [u.firstName, u.lastName].filter(Boolean).join(" ") ||
            u.email ||
            u.id,
        })),
      );
    } catch {
      setSupervisors([]);
    }
  }, []);

  useEffect(() => {
    fetchSupervisors();
  }, [fetchSupervisors]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSupervisorSearch(supervisorSearch),
      300,
    );
    return () => clearTimeout(timer);
  }, [supervisorSearch]);

  useEffect(() => {
    if (isModalOpen || isEditModalOpen)
      fetchSupervisors(debouncedSupervisorSearch);
  }, [
    isModalOpen,
    isEditModalOpen,
    debouncedSupervisorSearch,
    fetchSupervisors,
  ]);

  const fetchLocations = useCallback(async (search?: string) => {
    try {
      const res = await educationManagementApi.getLocations({
        page: 1,
        limit: 50,
        search: search?.trim() ?? "",
      });
      setLocations(
        (res.data ?? []).map((l) => ({
          id: l.id,
          name: l.name,
          address: l.address,
        })),
      );
    } catch {
      setLocations([]);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedLocationSearch(locationSearch),
      300,
    );
    return () => clearTimeout(timer);
  }, [locationSearch]);

  useEffect(() => {
    if (isModalOpen || isEditModalOpen)
      fetchLocations(debouncedLocationSearch);
  }, [isModalOpen, isEditModalOpen, debouncedLocationSearch, fetchLocations]);

  const typeFilterValue = (() => {
    const raw = filterValues.type as string | undefined;
    if (raw === "1") return 1;
    if (raw === "2") return 2;
    return undefined;
  })();

  const childCountOf = (d: Department) => d.children?.length ?? 0;

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  // Build rows for LISTE tab (paginated roots + expandable descendants)
  const listVisibleRows: TreeRow[] = useMemo(() => {
    if (isSearchMode) {
      // Backend already filtered by name/description; apply only type filter client-side.
      const matches = flatAll.filter(
        (d) => (typeFilterValue ? d.type === typeFilterValue : true),
      );
      return matches.map((d) => ({
        id: d.id,
        name: d.name,
        depth: 0,
        type: d.type,
        parentID: d.parentID ?? null,
        parentName: d.parentName,
        description: d.description,
        childCount: childCountOf(d),
        dept: d,
      }));
    }

    // Roots filtered by type
    const roots = tree.filter((d) =>
      typeFilterValue ? d.type === typeFilterValue : true,
    );

    const out: TreeRow[] = [];
    const walk = (node: Department, depth: number) => {
      out.push({
        id: node.id,
        name: node.name,
        depth,
        type: node.type,
        parentID: node.parentID ?? null,
        parentName: node.parentName,
        description: node.description,
        childCount: childCountOf(node),
        dept: node,
      });
      if (expandedIds.has(node.id) && node.children) {
        node.children.forEach((c) => walk(c, depth + 1));
      }
    };
    roots.forEach((r) => walk(r, 0));
    return out;
  }, [tree, flatAll, expandedIds, typeFilterValue, isSearchMode]);

  // Paginate LISTE visible rows (only at root level; expanded children travel with their parent)
  const listPaginatedRows: TreeRow[] = useMemo(() => {
    if (isSearchMode) {
      const start = (currentPage - 1) * pageSize;
      return listVisibleRows.slice(start, start + pageSize);
    }
    // Index roots; pagination slices by root, carrying expanded descendants
    const rootIndices: number[] = [];
    listVisibleRows.forEach((r, i) => {
      if (r.depth === 0) rootIndices.push(i);
    });
    const startRoot = (currentPage - 1) * pageSize;
    const endRoot = startRoot + pageSize;
    const startIdx = rootIndices[startRoot] ?? listVisibleRows.length;
    const endIdx =
      rootIndices[endRoot] !== undefined
        ? rootIndices[endRoot]
        : listVisibleRows.length;
    return listVisibleRows.slice(startIdx, endIdx);
  }, [listVisibleRows, currentPage, pageSize, isSearchMode]);

  const listTotalRoots = useMemo(() => {
    if (isSearchMode) return listVisibleRows.length;
    return listVisibleRows.filter((r) => r.depth === 0).length;
  }, [listVisibleRows, isSearchMode]);

  // Build rows for HIYERARŞI tab: organization root → Merkez/TAŞRA → real hierarchy
  const hierarchyRows: TreeRow[] = useMemo(() => {
    if (tree.length === 0 && !isLoading) return [];
    const centralRoots = tree.filter((d) => d.type === 1);
    const branchRoots = tree.filter((d) => d.type === 2);
    const centralCount = flatAll.filter((d) => d.type === 1).length;
    const branchCount = flatAll.filter((d) => d.type === 2).length;

    const out: TreeRow[] = [];
    out.push({
      id: VIRTUAL_ROOT_ID,
      name: ORG_NAME,
      depth: 0,
      childCount: centralCount + branchCount,
      isVirtual: true,
    });

    if (!expandedIds.has(VIRTUAL_ROOT_ID)) return out;

    const pushGroup = (
      groupId: string,
      label: string,
      count: number,
      roots: Department[],
    ) => {
      out.push({
        id: groupId,
        name: label,
        depth: 1,
        childCount: count,
        isVirtual: true,
      });
      if (!expandedIds.has(groupId)) return;
      const walk = (node: Department, depth: number) => {
        out.push({
          id: node.id,
          name: node.name,
          depth,
          type: node.type,
          parentID: node.parentID ?? null,
          parentName: node.parentName,
          description: node.description,
          childCount: childCountOf(node),
          dept: node,
        });
        if (expandedIds.has(node.id) && node.children) {
          node.children.forEach((c) => walk(c, depth + 1));
        }
      };
      roots.forEach((r) => walk(r, 2));
    };

    pushGroup(
      VIRTUAL_CENTRAL_ID,
      t("departments.typeCentral"),
      centralCount,
      centralRoots,
    );
    pushGroup(
      VIRTUAL_BRANCH_ID,
      t("departments.typeBranch"),
      branchCount,
      branchRoots,
    );
    return out;
  }, [tree, flatAll, expandedIds, isLoading, t]);

  // Auto-expand org root on hierarchy tab first visit
  useEffect(() => {
    if (isHierarchyTab && tree.length > 0) {
      setExpandedIds((prev) => {
        if (
          prev.has(VIRTUAL_ROOT_ID) &&
          prev.has(VIRTUAL_CENTRAL_ID) &&
          prev.has(VIRTUAL_BRANCH_ID)
        )
          return prev;
        const n = new Set(prev);
        n.add(VIRTUAL_ROOT_ID);
        n.add(VIRTUAL_CENTRAL_ID);
        n.add(VIRTUAL_BRANCH_ID);
        return n;
      });
    }
  }, [isHierarchyTab, tree.length]);

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleSearch = (value: string) => setSearchTerm(value);

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  const rewriteDepartmentError = (err: unknown, name?: string): string | null => {
    const raw =
      (err as {
        response?: { data?: { message?: string; messages?: string[] } };
      })?.response?.data;
    const msg =
      Array.isArray(raw?.messages) && raw.messages.length > 0
        ? raw.messages.join(", ")
        : raw?.message;
    if (!msg) return null;
    // Backend halen şablon `{name}` placeholder'ı ile gönderiyorsa
    // burada gerçek değerle dolduruyoruz.
    if (msg.includes("{name}") && name) {
      return msg.split("{name}").join(name);
    }
    // Kullanıcı kendi birimini üst departman olarak seçtiğinde gelen
    // "döngüsel referans" mesajını daha doğru bir metinle değiştir.
    if (/(?:döngüsel referans|kendi alt departman)/i.test(msg)) {
      return t("departmentsPage.selfParentError");
    }
    return msg;
  };

  const handleAddDepartment = async (payload: CreateDepartmentPayload) => {
    try {
      await departmentsApi.create(payload);
      toast.success(t("departments.createSuccess"));
      setIsModalOpen(false);
      fetchTree();
    } catch (err) {
      const rewritten = rewriteDepartmentError(err, payload.name);
      toast.error(
        rewritten ??
          t("departments.createError"),
      );
    }
  };

  const handleEditClick = (row: TreeRow) => {
    if (!row.dept) return;
    setEditTarget(row.dept);
    setIsEditModalOpen(true);
    handleDropdownToggle(row.id, false);
  };

  const handleUpdateDepartment = async (payload: CreateDepartmentPayload) => {
    if (!editTarget) return;
    try {
      await departmentsApi.update(editTarget.id, payload);
      toast.success(t("departments.updateSuccess"));
      setEditTarget(null);
      setIsEditModalOpen(false);
      fetchTree();
    } catch (err) {
      const rewritten = rewriteDepartmentError(err, payload.name);
      toast.error(
        rewritten ??
          t("departments.updateError"),
      );
    }
  };

  const handleDeleteClick = (row: TreeRow) => {
    if (!row.dept) return;
    setDeleteTarget(row.dept);
    handleDropdownToggle(row.id, false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await departmentsApi.delete(deleteTarget.id);
      toast.success(t("departments.deleteSuccess"));
      setDeleteTarget(null);
      fetchTree();
    } catch {
      /* toast handled by interceptor */
    } finally {
      setIsDeleting(false);
    }
  };

  const getTypeLabel = (type?: number) => {
    if (type === 1) return t("departments.typeCentral");
    if (type === 2) return t("departments.typeBranch");
    return "—";
  };

  const filterFields: FilterField[] = [
    {
      name: "type",
      label: t("departments.type"),
      type: "dropdown",
      placeholder: t("departments.selectType"),
      options: [
        { label: t("departments.filterAll"), value: "" },
        { label: t("departments.typeCentral"), value: "1" },
        { label: t("departments.typeBranch"), value: "2" },
      ],
    },
  ];

  const renderNameCell = (row: TreeRow) => {
    const isExpanded = expandedIds.has(row.id);
    const canExpand = isHierarchyTab
      ? row.childCount > 0 || row.isVirtual === true
      : !isSearchMode && row.childCount > 0;
    const isVirtual = !!row.isVirtual;
    const isRoot = row.depth === 0 || (!isVirtual && !row.parentID);

    return (
      <div
        className={styles.treeCell}
        style={{ paddingLeft: row.depth * 24 }}
      >
        {row.depth > 0 && <span className={styles.treeGuide} aria-hidden />}
        {canExpand ? (
          <button
            type="button"
            className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(row.id);
            }}
            aria-label={isExpanded ? t("departmentsPage.collapse") : t("departmentsPage.expand")}
          >
            {isExpanded ? (
              <ArrowDownIcon width={14} height={14} />
            ) : (
              <ArrowRightIcon width={14} height={14} />
            )}
          </button>
        ) : (
          <span className={styles.chevronPlaceholder} />
        )}
        <span
          className={`${styles.iconWrap} ${isVirtual ? styles.iconOrg : isRoot ? styles.iconRoot : styles.iconLeaf}`}
        >
          <DepartmentIcon width={16} height={16} />
        </span>
        <span
          className={
            isVirtual
              ? styles.virtualName
              : isRoot
                ? styles.rootCategoryName
                : styles.subCategoryName
          }
        >
          {row.name}
        </span>
        {!isVirtual && row.type !== undefined && (
          <span
            className={`${styles.typeChip} ${row.type === 1 ? styles.typeCentralChip : styles.typeBranchChip}`}
          >
            {getTypeLabel(row.type)}
          </span>
        )}
        {row.childCount > 0 && (
          <span
            className={styles.childCountBadge}
            title={`${row.childCount} alt birim`}
          >
            {row.childCount}
          </span>
        )}
        {isSearchMode && row.parentName && !isHierarchyTab && (
          <span className={styles.breadcrumb}>in {row.parentName}</span>
        )}
      </div>
    );
  };

  const columns: TableColumn<TreeRow>[] = [
    {
      header: t("departments.columns.name"),
      accessor: renderNameCell,
      className: styles.nameColumn,
    },
    {
      header: t("departments.columns.description"),
      accessor: (row) =>
        row.isVirtual ? (
          ""
        ) : (
          <span className={styles.descriptionCell} title={row.description ?? ""}>
            {row.description ?? "—"}
          </span>
        ),
      className: styles.descriptionColumn,
    },
  ];

  const renderActions = (row: TreeRow) => {
    if (row.isVirtual) return null;
    if (!canUpdate && !canDelete) return null;
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;
    const menuContent = (
      <div className={styles.actionMenu}>
        {canUpdate && (
          <button
            className={styles.actionMenuItem}
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row);
            }}
          >
            <EditIcon width={20} height={20} />
            {t("buttons.edit")}
          </button>
        )}
        {canDelete && (
          <button
            className={`${styles.actionMenuItem} ${styles.error}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
          >
            <DeleteIcon width={20} height={20} />
            {t("buttons.delete")}
          </button>
        )}
      </div>
    );
    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  const tableData = isHierarchyTab ? hierarchyRows : listPaginatedRows;
  const tableTotal = isHierarchyTab ? hierarchyRows.length : listTotalRoots;

  return (
    <DashboardLayout>
      <Header title={t("departments.title")} />
      <div className={styles.tabBar}>
        <TabGroup
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={[
            { label: t("departments.tabs.list"), content: null },
            { label: t("departments.tabs.hierarchy"), content: null },
          ]}
        />
      </div>
      <PageHeader
        searchPlaceholder={t("departments.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearch}
        filters={isHierarchyTab ? [] : filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canCreate ? () => setIsModalOpen(true) : undefined}
        addButtonLabel={
          canCreate ? t("departments.addButton") : undefined
        }
      />
      <Table
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        totalRecords={tableTotal}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("departments.noData")}
      />
      <DepartmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddDepartment}
        departments={flatAll}
        supervisors={supervisors}
        onSearchSupervisors={setSupervisorSearch}
        locations={locations}
        onSearchLocations={setLocationSearch}
      />
      <DepartmentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditTarget(null);
        }}
        onSubmit={handleUpdateDepartment}
        departments={flatAll}
        supervisors={supervisors}
        onSearchSupervisors={setSupervisorSearch}
        locations={locations}
        onSearchLocations={setLocationSearch}
        editDepartment={editTarget}
        title={t("departments.editTitle")}
      />
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t("departments.deleteConfirmTitle")}
        message={
          deleteTarget
            ? t(
                "departments.deleteConfirmMessage",
                `"{{name}}" birimini silmek istediğinize emin misiniz?`,
                { name: deleteTarget.name },
              )
            : ""
        }
        confirmText={t("buttons.delete")}
        loading={isDeleting}
        confirmVariant="danger"
      />
    </DashboardLayout>
  );
};

export default DepartmentsPage;
